/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useProfiles } from '../context/ProfileContext'
import { useAuth } from '../context/AuthContext'
import { openAuthModal } from '../context/AuthContext'
import SimilarBrandsModal from './SimilarBrandsModal'
import './BrandCard.css'

const PRICE_LABELS = { '1': '$', '2': '$$', '3': '$$$', '4': '$$$$', '5': '$$$$$' }
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL

function getLogoUrl(brandName) {
    const filename = brandName
        .toLowerCase()
        .replace(/[&']/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-{2,}/g, '-')
    return `${SUPABASE_URL}/storage/v1/object/public/logos/${filename}.png`
}

function getImageUrls(brandName) {
    const filename = brandName
        .toLowerCase()
        .replace(/[&']/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-{2,}/g, '-')
    return [1, 2, 3, 4, 5].map(n =>
        `${SUPABASE_URL}/storage/v1/object/public/brand-images/${filename}-${n}.jpg`
    )
}

function BrandCard({ brand, score, rank, showSimilar = false }) {
    const { user } = useAuth()
    const { isSaved, isDisliked, toggleSave, toggleDislike, activeProfileId, defaultProfile } = useProfiles()
    // Use active profile, or fall back to default profile (My Brands) in scratch mode
    const effectiveProfileId = activeProfileId || defaultProfile?.id
    const priceLabel = PRICE_LABELS[brand.price_range] || ''
    const saved = isSaved(brand.id)
    const disliked = isDisliked(brand.id)
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [hasImages, setHasImages] = useState(false)
    const [logoFailed, setLogoFailed] = useState(false)
    const [showSimilarModal, setShowSimilarModal] = useState(false)
    const imageUrls = getImageUrls(brand.name)
    const nameRef = useRef(null)
    const [nameFontSize, setNameFontSize] = useState(16)

    useLayoutEffect(() => {
        const el = nameRef.current
        if (!el) return
        const parent = el.parentElement
        if (!parent) return

        // Measure natural text width at 16px using a canvas for accuracy
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        ctx.font = `500 16px ${getComputedStyle(el).fontFamily}`
        const textWidth = ctx.measureText(brand.name).width

        // Available width = parent width minus price tag (approx 30px)
        const availableWidth = parent.clientWidth - 36

        const overflows = textWidth > availableWidth
        el.style.fontSize = overflows ? '13px' : '16px'
        setNameFontSize(overflows ? 13 : 16)
    }, [brand.name])

    // Probe for first image silently — only show carousel if it exists
    useState(() => {
        const img = new Image()
        img.onload = () => setHasImages(true)
        img.src = imageUrls[0]
    })

    const nextImage = (e) => {
        e.stopPropagation()
        setCurrentImageIndex(prev => (prev + 1) % imageUrls.length)
    }

    const prevImage = (e) => {
        e.stopPropagation()
        setCurrentImageIndex(prev => (prev - 1 + imageUrls.length) % imageUrls.length)
    }

    return (
        <div className={`brand-card ${disliked ? 'disliked' : ''}`}>
            {/* Image carousel — only shows when brand has product images */}
            {hasImages && (
                <div className="brand-image-carousel">
                    <img
                        src={imageUrls[currentImageIndex]}
                        alt={`${brand.name} product`}
                        className="brand-image"
                        onError={() => setHasImages(false)}
                    />
                    <button className="carousel-btn carousel-prev" onClick={prevImage}>‹</button>
                    <button className="carousel-btn carousel-next" onClick={nextImage}>›</button>
                    <div className="carousel-dots">
                        {imageUrls.map((_, i) => (
                            <span
                                key={i}
                                className={`carousel-dot ${i === currentImageIndex ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i) }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Main content: logo panel + info panel side by side */}
            <div className="brand-card-body">
                <div className="brand-logo-panel">
                    {!logoFailed ? (
                        <img
                            src={getLogoUrl(brand.name)}
                            alt={brand.name}
                            className="brand-logo"
                            onError={() => setLogoFailed(true)}
                        />
                    ) : (
                        <div className="brand-logo-fallback">
                            {brand.name.charAt(0)}
                        </div>
                    )}
                </div>

                <div className="brand-info-panel">
                    <div className="brand-info-top">
                        <div className="brand-name-row">
                            <h3
                                className="brand-name"
                                ref={nameRef}
                                style={{ fontSize: `${nameFontSize}px` }}
                            >{brand.name}</h3>
                            {priceLabel && <span className="brand-price">{priceLabel}</span>}
                        </div>
                        <div className="brand-meta">
                            {rank && <span className="brand-rank">#{rank}</span>}
                            {brand.country && <span className="brand-country">{brand.country}</span>}
                        </div>
                    </div>
                    {brand.description && <p className="brand-description">{brand.description}</p>}
                </div>
            </div>

            {/* Footer with actions */}
            <div className="brand-card-footer">
                <div className="brand-actions">
                    {brand.website_url && (
                        <a href={brand.website_url} target="_blank" rel="noopener noreferrer" className="visit-btn">
                            Visit
                        </a>
                    )}
                    <button
                        className={`save-btn ${saved ? 'saved' : ''}`}
                        onClick={() => user && effectiveProfileId ? toggleSave(brand.id) : openAuthModal()}
                    >
                        {saved ? 'Saved' : 'Save'}
                    </button>
                    <button
                        className={`dislike-btn ${disliked ? 'disliked' : ''}`}
                        onClick={() => user && effectiveProfileId ? toggleDislike(brand.id) : openAuthModal()}
                    >
                        Not for me
                    </button>
                </div>
                {showSimilar && (
                    <button
                        className="find-similar-btn"
                        onClick={() => setShowSimilarModal(true)}
                    >
                        Find similar brands →
                    </button>
                )}
            </div>

            {showSimilarModal && (
                <SimilarBrandsModal
                    brand={brand}
                    onClose={() => setShowSimilarModal(false)}
                />
            )}
        </div>
    )
}

export default BrandCard
